import React, { useEffect, useState } from "react";
import { Modal, message } from "antd";
import CryptoJS from "crypto-js";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPen } from "@fortawesome/free-solid-svg-icons";
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';

import { AntdButton, InputField } from "../../../blueplanitUtils/InputField";

import {
  addEmail,
  editEmail,
  deleteEmail,
  fetchEmails,
} from "../../../Slices/salesEmailsSlice";

import {
  addEmail as addSharedEmail,
  editEmail as editSharedEmail,
  deleteEmail as deleteSharedEmail,
  clearEmailError,
} from "../../../Slices/sharedEmailSlice";

import { setLoader } from "../../../Slices/loaderSlice";

const Mails = () => {
  const dispatch = useDispatch();

  // normal email slice
  const emails = useSelector((state) => state.salesEmails.emails);

  // shared email slice
  const { sharedemails, loading } = useSelector((state) => state.sharedEmail);

  // states for private email modal
  const [isPrivateModalVisible, setIsPrivateModalVisible] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [confirmPasswordInput, setConfirmPasswordInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPrivateEditMode, setIsPrivateEditMode] = useState(false);
  const [editingPrivateEmailId, setEditingPrivateEmailId] = useState(null);

  // states for shared email modal
  const [isSharedModalVisible, setIsSharedModalVisible] = useState(false);
  const [sharedEmailInput, setSharedEmailInput] = useState("");
  const [isSharedEditMode, setIsSharedEditMode] = useState(false);
  const [editingSharedEmailId, setEditingSharedEmailId] = useState(null);

  const encryptPassword = (password) => {
    const secretkey = process.env.REACT_APP_SECRET_KEY || 'bp-dashboard-key';
    return CryptoJS.AES.encrypt(password, secretkey).toString();
  };

  useEffect(() => {
    if (!emails || emails.length === 0) {
      dispatch(fetchEmails());
    }
  }, []);

  // ------------------ PRIVATE EMAIL HANDLERS --------------------
  const showPrivateModal = (emailObj = null) => {
    if (emailObj && emailObj.email) {
      setEmailInput(emailObj.email);
      setPasswordInput("");
      setConfirmPasswordInput("");
      setEditingPrivateEmailId(emailObj._id);
      setIsPrivateEditMode(true);
    } else {
      setEmailInput("");
      setPasswordInput("");
      setConfirmPasswordInput("");
      setEditingPrivateEmailId(null);
      setIsPrivateEditMode(false);
    }
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsPrivateModalVisible(true);
  };

  const handleCancelPrivate = () => {
    setIsPrivateModalVisible(false);
    setEmailInput("");
    setPasswordInput("");
    setConfirmPasswordInput("");
    setEditingPrivateEmailId(null);
    setIsPrivateEditMode(false);
  };

  const handleSubmitPrivateEmail = async () => {
    const trimmedEmail = emailInput.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!trimmedEmail) return message.error("Email cannot be empty");
    if (!emailPattern.test(trimmedEmail))
      return message.error("Please enter a valid email (e.g. example@domain.com)");
    if (!passwordInput) return message.error("Password cannot be empty");
    if (passwordInput !== confirmPasswordInput)
      return message.error("Passwords do not match");

    const existingEmail = emails.find(
      (e) => e.email.toLowerCase() === trimmedEmail.toLowerCase()
    );

    if (!isPrivateEditMode && existingEmail) return message.error("Email already exists");

    try {
      dispatch(setLoader(true));
      const encryptedPassword = encryptPassword(passwordInput);

      if (isPrivateEditMode) {
        await dispatch(
          editEmail({ _id: editingPrivateEmailId, email: trimmedEmail, password: encryptedPassword })
        ).unwrap();
      } else {
        await dispatch(addEmail({ email: trimmedEmail, password: encryptedPassword })).unwrap();
      }
    } catch (error) {
      console.error("Private email error:", error);
    } finally {
      dispatch(setLoader(false));
      handleCancelPrivate();
    }
  };

  const handleDeletePrivate = async (email) => {
    await dispatch(deleteEmail({ email }));
  };

  // ------------------ SHARED EMAIL HANDLERS --------------------
  const showSharedModal = (emailObj = null) => {
    if (emailObj) {
      setSharedEmailInput(emailObj.email);
      setEditingSharedEmailId(emailObj._id);
      setIsSharedEditMode(true);
    } else {
      setSharedEmailInput("");
      setEditingSharedEmailId(null);
      setIsSharedEditMode(false);
    }
    setIsSharedModalVisible(true);
  };

  const handleCancelShared = () => {
    setIsSharedModalVisible(false);
    setSharedEmailInput("");
    setEditingSharedEmailId(null);
    setIsSharedEditMode(false);
    dispatch(clearEmailError());
  };

  const handleSubmitSharedEmail = async () => {
    const trimmedEmail = sharedEmailInput.trim();
    if (!trimmedEmail) return message.error("Email cannot be empty");

    const existingEmail = sharedemails?.find(
      (e) => e?.email?.toLowerCase() === trimmedEmail.toLowerCase()
    );

    if (!isSharedEditMode && existingEmail) return message.error("Email already exists");

    try {
      if (isSharedEditMode) {
        const result = await dispatch(
          editSharedEmail({ id: editingSharedEmailId, email: trimmedEmail })
        );
        result.meta.requestStatus === "fulfilled"
          ? message.success("Email updated successfully")
          : message.error("Failed to update email");
      } else {
        const result = await dispatch(addSharedEmail(trimmedEmail));
        result.meta.requestStatus === "fulfilled"
          ? message.success("Email added successfully")
          : message.error("Failed to add email");
      }
    } catch {
      message.error("Error occurred.");
    }
    handleCancelShared();
  };

  const handleDeleteShared = async (emailObj) => {
    const result = await dispatch(deleteSharedEmail({ id: emailObj._id, email: emailObj.email }));
    result.meta.requestStatus === "fulfilled"
      ? message.success("Email deleted successfully")
      : message.error("Delete failed");
  };

  return (
    <div style={{ marginTop: 60, padding: 20, minHeight: "100vh", background: "#f5f5f5" }}>

      {/* PRIVATE EMAIL CARD */}
      <div style={{
        background: "#fff",
        padding: 20,
        borderRadius: 8,
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        marginBottom: 30
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
          <h2>Mails</h2>
          <AntdButton type="primary" label="Add" onClick={() => showPrivateModal()} />
        </div>

        {emails?.length === 0 ? <p>No emails found.</p> :
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {emails?.filter((e) => e.status === true || e.status === "true").map((emailObj) => (
              <div key={emailObj._id}
                style={{
                  display: "flex", alignItems: "center", padding: 10,
                  border: "1px solid #ddd", borderRadius: 6
                }}>
                <div style={{ flex: 1 }}>
                  <b>Email:</b> {emailObj.email}
                </div>
                <FontAwesomeIcon icon={faPen} style={{ color: "red", cursor: "pointer" }}
                  onClick={() => showPrivateModal(emailObj)} />
                <FontAwesomeIcon icon={faTrash} style={{ color: "red", cursor: "pointer", marginLeft: 12 }}
                  onClick={() => handleDeletePrivate(emailObj.email)} />
              </div>
            ))}
          </div>
        }
      </div>

      {/* SHARED EMAIL CARD */}
      <div style={{
        background: "#fff",
        padding: 20,
        borderRadius: 8,
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
          <h2>Shared Mails</h2>
          <AntdButton type="primary" label="Add" onClick={() => showSharedModal()} />
        </div>

        {loading ? <p>Loading...</p> :
          sharedemails?.length === 0 ? <p>No shared emails found.</p> :
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {sharedemails
                ?.filter((emailObj) => emailObj.status === true || emailObj.status === "true")
                ?.map((emailObj) => (
                  <div key={emailObj._id}
                    style={{
                      display: "flex", alignItems: "center", padding: 10,
                      border: "1px solid #ddd", borderRadius: 6
                    }}>
                    <div style={{ flex: 1 }}>
                      <b>Email:</b> {emailObj.email}
                    </div>
                    <FontAwesomeIcon icon={faPen}
                      onClick={() => showSharedModal(emailObj)} style={{ color: "red", cursor: "pointer" }} />
                    <FontAwesomeIcon icon={faTrash}
                      onClick={() => handleDeleteShared(emailObj)}
                      style={{ color: "red", cursor: "pointer", marginLeft: 12 }} />
                  </div>
                ))}
            </div>
        }
      </div>

      {/* Private Email Modal */}
      <Modal
        title={isPrivateEditMode ? "Edit Email" : "Add Email"}
        open={isPrivateModalVisible}
        onOk={handleSubmitPrivateEmail}
        onCancel={handleCancelPrivate}
        okText={isPrivateEditMode ? "Update" : "Add"}
      >
        <InputField
          disabled={isPrivateEditMode}
          placeholder="Enter email"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
        />

        <div style={{ marginTop: 16, position: "relative" }}>
          <InputField
            type={showPassword ? "text" : "password"}
            placeholder="Enter password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            style={{ paddingRight: 40 }}
          />
          <span
            onClick={() => setShowPassword(!showPassword)}
            style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", cursor: "pointer" }}>
            {showPassword ? <FontAwesomeIcon icon={faEye} /> : <FontAwesomeIcon icon={faEyeSlash} />}
          </span>
        </div>

        <div style={{ marginTop: 16, position: "relative" }}>
          <InputField
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm password"
            value={confirmPasswordInput}
            onChange={(e) => setConfirmPasswordInput(e.target.value)}
            style={{ paddingRight: 40 }}
          />
          <span
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", cursor: "pointer" }}>
            {showConfirmPassword ? <FontAwesomeIcon icon={faEye} /> : <FontAwesomeIcon icon={faEyeSlash} />}
          </span>
        </div>
      </Modal>

      {/* Shared Email Modal */}
      <Modal
        title={isSharedEditMode ? "Edit Shared Email" : "Add Shared Email"}
        open={isSharedModalVisible}
        onOk={handleSubmitSharedEmail}
        onCancel={handleCancelShared}
        okText={isSharedEditMode ? "Update" : "Add"}
      >
        <InputField
          placeholder="Enter email address"
          value={sharedEmailInput}
          onChange={(e) => setSharedEmailInput(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default Mails;
